/**
 * Event Sourcing System for Timeline, Undo/Redo, and Sync
 *
 * This module provides a unified event sourcing architecture for all state changes.
 * All mutations (reveal/hide tiles, add/remove icons, etc.) are stored as immutable
 * events in a chronological history. Current state is derived by replaying events.
 *
 * Benefits:
 * - Undo/Redo: Navigate through event history
 * - Time Travel: Replay events up to a specific game date
 * - Sync: Broadcast small events instead of full state
 * - Audit Trail: Complete history of all changes
 */

import { DEFAULT_GAME_DATE } from './calendar.js';

/**
 * Event Types
 */
export const EventType = Object.freeze({
  // Fog of War
  REVEAL_HEX: 'REVEAL_HEX',
  HIDE_HEX: 'HIDE_HEX',
  REVEAL_ALL: 'REVEAL_ALL',
  HIDE_ALL: 'HIDE_ALL',

  // Icons
  ADD_ICON: 'ADD_ICON',
  REMOVE_ICON: 'REMOVE_ICON',

  // Notes
  ADD_NOTE: 'ADD_NOTE',
  EDIT_NOTE: 'EDIT_NOTE',
  REMOVE_NOTE: 'REMOVE_NOTE',

  // Tokens
  ADD_TOKEN: 'ADD_TOKEN',
  MOVE_TOKEN: 'MOVE_TOKEN',
  EDIT_TOKEN: 'EDIT_TOKEN',
  REMOVE_TOKEN: 'REMOVE_TOKEN',

  // Calendar
  ADVANCE_CALENDAR: 'ADVANCE_CALENDAR',
  SET_CALENDAR: 'SET_CALENDAR',
  ADD_BOOKMARK: 'ADD_BOOKMARK',
  REMOVE_BOOKMARK: 'REMOVE_BOOKMARK'
});

/**
 * Initialize event sourcing for a session
 * @param {Object} session - Session object
 */
export function initializeEventSourcing(session) {
  if (!session.history) {
    session.history = [];
  }

  if (session.currentEventId === undefined) {
    session.currentEventId = 0;
  }

  if (session.timeTravelEventId === undefined) {
    session.timeTravelEventId = null;
  }

  if (!session.undoStack) {
    session.undoStack = [];
  }

  if (!session.redoStack) {
    session.redoStack = [];
  }
}

/**
 * Create a new event
 * @param {string} type - Event type from EventType enum
 * @param {Object} data - Event data
 * @param {Object} gameDate - Current game date
 * @returns {Object} Event object
 */
export function createEvent(type, data, gameDate) {
  return {
    type,
    ...data,
    gameDate: gameDate || DEFAULT_GAME_DATE,
    timestamp: Date.now()
  };
}

/**
 * Add an event to session history and apply it to state
 * @param {Object} session - Session object
 * @param {Object} eventData - Event data (type + payload)
 * @returns {Object} The created event
 */
export function addEvent(session, eventData) {
  // If we've undone some events, discard the redo stack
  // (creating a new timeline branch)
  if (session.redoStack && session.redoStack.length > 0) {
    // Remove events that are now in alternate timeline
    session.history = session.history.filter(e => e.id <= session.currentEventId);
    session.redoStack = [];
  }

  // Create new event with incremental ID
  const nextId = session.currentEventId + 1;
  const event = {
    id: nextId,
    ...createEvent(
      eventData.type,
      eventData,
      session.calendar?.currentDate || DEFAULT_GAME_DATE
    )
  };

  // Add to history
  session.history.push(event);

  // Update current event
  if (session.undoStack) {
    session.undoStack.push(session.currentEventId);
  }
  session.currentEventId = event.id;

  // Apply event to state
  applyEvent(session, event);

  // Mark session as modified
  session.lastModified = Date.now();

  return event;
}

/**
 * Apply a single event to session state
 * @param {Object} session - Session object (will be mutated)
 * @param {Object} event - Event to apply
 */
export function applyEvent(session, event) {
  switch (event.type) {
    case EventType.REVEAL_HEX:
      ensureHexState(session, event.coord);
      session.hexStates[event.coord].revealed = true;
      break;

    case EventType.HIDE_HEX:
      ensureHexState(session, event.coord);
      session.hexStates[event.coord].revealed = false;
      break;

    case EventType.REVEAL_ALL:
      Object.keys(session.hexStates).forEach(coord => {
        session.hexStates[coord].revealed = true;
      });
      break;

    case EventType.HIDE_ALL:
      Object.keys(session.hexStates).forEach(coord => {
        session.hexStates[coord].revealed = false;
      });
      break;

    case EventType.ADD_ICON:
      ensureHexState(session, event.coord);
      if (!session.hexStates[event.coord].icons) {
        session.hexStates[event.coord].icons = [];
      }
      session.hexStates[event.coord].icons.push({
        id: event.iconId,
        name: event.iconName,
        color: event.iconColor
      });
      break;

    case EventType.REMOVE_ICON:
      ensureHexState(session, event.coord);
      if (session.hexStates[event.coord].icons) {
        session.hexStates[event.coord].icons =
          session.hexStates[event.coord].icons.filter(i => i.id !== event.iconId);
      }
      break;

    case EventType.ADD_NOTE:
      ensureHexState(session, event.coord);
      if (!session.hexStates[event.coord].notes) {
        session.hexStates[event.coord].notes = [];
      }
      session.hexStates[event.coord].notes.push({
        id: event.noteId,
        text: event.noteText
      });
      break;

    case EventType.EDIT_NOTE:
      ensureHexState(session, event.coord);
      if (session.hexStates[event.coord].notes) {
        const note = session.hexStates[event.coord].notes.find(n => n.id === event.noteId);
        if (note) {
          note.text = event.newText;
        }
      }
      break;

    case EventType.REMOVE_NOTE:
      ensureHexState(session, event.coord);
      if (session.hexStates[event.coord].notes) {
        session.hexStates[event.coord].notes =
          session.hexStates[event.coord].notes.filter(n => n.id !== event.noteId);
      }
      break;

    case EventType.ADD_TOKEN:
      if (!session.tokens) {
        session.tokens = [];
      }
      session.tokens.push({
        id: event.tokenId,
        name: event.tokenName,
        coord: event.coord,
        icon: event.tokenIcon,
        color: event.tokenColor
      });
      break;

    case EventType.MOVE_TOKEN:
      if (session.tokens) {
        const token = session.tokens.find(t => t.id === event.tokenId);
        if (token) {
          token.coord = event.toCoord;
        }
      }
      break;

    case EventType.EDIT_TOKEN:
      if (session.tokens) {
        const token = session.tokens.find(t => t.id === event.tokenId);
        if (token && event.changes) {
          Object.assign(token, event.changes);
        }
      }
      break;

    case EventType.REMOVE_TOKEN:
      if (session.tokens) {
        session.tokens = session.tokens.filter(t => t.id !== event.tokenId);
      }
      break;

    case EventType.ADVANCE_CALENDAR:
      if (session.calendar) {
        session.calendar.currentDate = event.newDate;
        session.calendar.daysElapsed = (session.calendar.daysElapsed || 1) + event.days;
      }
      break;

    case EventType.SET_CALENDAR:
      if (session.calendar) {
        session.calendar.currentDate = event.newDate;
        session.calendar.daysElapsed = event.daysElapsed;
      }
      break;

    case EventType.ADD_BOOKMARK:
      if (session.calendar && session.calendar.events) {
        session.calendar.events.push({
          id: event.bookmarkId,
          name: event.bookmarkName,
          date: event.date,
          description: event.description || ''
        });
      }
      break;

    case EventType.REMOVE_BOOKMARK:
      if (session.calendar && session.calendar.events) {
        session.calendar.events = session.calendar.events.filter(e => e.id !== event.bookmarkId);
      }
      break;

    default:
      console.warn('Unknown event type:', event.type);
  }
}

/**
 * Ensure hex state exists in session
 * @param {Object} session - Session object
 * @param {string} coord - Hex coordinate (e.g., "3,5")
 */
function ensureHexState(session, coord) {
  if (!session.hexStates) {
    session.hexStates = {};
  }

  if (!session.hexStates[coord]) {
    session.hexStates[coord] = {
      revealed: false,
      icons: [],
      notes: []
    };
  }
}

/**
 * Rebuild session state by replaying events from history
 * @param {Object} session - Session with history
 * @param {number|null} upToEventId - Replay up to this event (null = all events)
 */
export function rebuildState(session, upToEventId = null) {
  console.log('[rebuildState] Called with upToEventId:', upToEventId);
  console.log('[rebuildState] Current hexStates count:', Object.keys(session.hexStates || {}).length);

  // Store initial hexStates structure (all coordinates with revealed: false)
  const initialHexStates = {};
  for (const coord in session.hexStates) {
    initialHexStates[coord] = {
      revealed: false,
      icons: [],
      notes: []
    };
  }

  console.log('[rebuildState] Initial hexStates count:', Object.keys(initialHexStates).length);

  // Reset state to initial
  session.hexStates = initialHexStates;
  session.tokens = [];
  session.calendar = {
    campaignStartDate: session.calendar?.campaignStartDate || DEFAULT_GAME_DATE,
    currentDate: session.calendar?.campaignStartDate || DEFAULT_GAME_DATE,
    daysElapsed: 1,
    events: []
  };

  // Get events to replay
  const events = upToEventId !== null
    ? session.history.filter(e => e.id <= upToEventId)
    : session.history.filter(e => e.id <= session.currentEventId);

  console.log('[rebuildState] Replaying', events.length, 'events');

  // Replay events in order
  for (const event of events) {
    applyEvent(session, event);
  }

  console.log('[rebuildState] Final hexStates count:', Object.keys(session.hexStates).length);
  console.log('[rebuildState] Sample hexState:', Object.values(session.hexStates)[0]);
}

/**
 * Undo the last action
 * @param {Object} session - Session object
 * @returns {boolean} True if undo was successful
 */
export function undo(session) {
  if (!session.undoStack || session.undoStack.length === 0) {
    return false;
  }

  // Pop from undo stack
  const previousEventId = session.undoStack.pop();

  // Push current state to redo stack
  if (!session.redoStack) {
    session.redoStack = [];
  }
  session.redoStack.push(session.currentEventId);

  // Set current event
  session.currentEventId = previousEventId;

  // Rebuild state up to this event
  rebuildState(session, previousEventId);

  // Mark as modified
  session.lastModified = Date.now();

  return true;
}

/**
 * Redo a previously undone action
 * @param {Object} session - Session object
 * @returns {boolean} True if redo was successful
 */
export function redo(session) {
  if (!session.redoStack || session.redoStack.length === 0) {
    return false;
  }

  // Pop from redo stack
  const nextEventId = session.redoStack.pop();

  // Push to undo stack
  if (!session.undoStack) {
    session.undoStack = [];
  }
  session.undoStack.push(session.currentEventId);

  // Set current event
  session.currentEventId = nextEventId;

  // Rebuild state up to this event
  rebuildState(session, nextEventId);

  // Mark as modified
  session.lastModified = Date.now();

  return true;
}

/**
 * Check if undo is available
 * @param {Object} session - Session object
 * @returns {boolean}
 */
export function canUndo(session) {
  return session.undoStack && session.undoStack.length > 0;
}

/**
 * Check if redo is available
 * @param {Object} session - Session object
 * @returns {boolean}
 */
export function canRedo(session) {
  return session.redoStack && session.redoStack.length > 0;
}

/**
 * Find the last event on or before a specific game date
 * @param {Array} history - Event history
 * @param {Object} targetDate - Target game date {year, month, day}
 * @returns {number|null} Event ID or null if no events before date
 */
export function findLastEventBeforeDate(history, targetDate) {
  // Convert dates to comparable numbers (YYYYMMDD format)
  const targetValue = dateToNumber(targetDate);

  let lastEventId = null;

  for (const event of history) {
    if (!event.gameDate) continue;

    const eventValue = dateToNumber(event.gameDate);

    if (eventValue <= targetValue) {
      lastEventId = event.id;
    } else {
      break; // Events are chronological, so we can stop
    }
  }

  return lastEventId;
}

/**
 * Convert game date to comparable number
 * @param {Object} date - Game date {year, month, day}
 * @returns {number} YYYYMMDD format
 */
function dateToNumber(date) {
  return (date.year * 10000) + (date.month * 100) + date.day;
}

/**
 * Migrate old reveal history format to events
 * @param {Object} session - Session with old format hexStates.revealHistory
 */
export function migrateRevealHistoryToEvents(session) {
  if (!session.hexStates) return;

  // Initialize event sourcing if needed
  initializeEventSourcing(session);

  // Track if we need to rebuild
  let eventAdded = false;

  // For each hex with reveal history
  for (const [coord, hexState] of Object.entries(session.hexStates)) {
    if (!hexState.revealHistory || hexState.revealHistory.length === 0) {
      // No reveal history, but check if it has revealed boolean
      if (hexState.revealed === true) {
        // Create a single REVEAL_HEX event at the start date
        const event = {
          id: session.history.length + 1,
          type: EventType.REVEAL_HEX,
          coord,
          gameDate: session.calendar?.campaignStartDate || DEFAULT_GAME_DATE,
          timestamp: session.created || Date.now()
        };
        session.history.push(event);
        eventAdded = true;
      }
      continue;
    }

    // Convert each reveal history entry to an event
    for (const entry of hexState.revealHistory) {
      const event = {
        id: session.history.length + 1,
        type: entry.revealed ? EventType.REVEAL_HEX : EventType.HIDE_HEX,
        coord,
        gameDate: entry.date || DEFAULT_GAME_DATE,
        timestamp: session.created || Date.now()
      };
      session.history.push(event);
      eventAdded = true;
    }
  }

  // Sort history by date and timestamp
  if (eventAdded) {
    session.history.sort((a, b) => {
      const aDate = dateToNumber(a.gameDate);
      const bDate = dateToNumber(b.gameDate);
      if (aDate !== bDate) return aDate - bDate;
      return a.timestamp - b.timestamp;
    });

    // Reassign IDs in order
    session.history.forEach((event, index) => {
      event.id = index + 1;
    });

    // Set current event to last
    session.currentEventId = session.history.length;

    // Clean up: delete old revealHistory fields after migration
    for (const hexState of Object.values(session.hexStates)) {
      if (hexState.revealHistory) {
        delete hexState.revealHistory;
      }
    }
  }
}

/**
 * Migrate icons and notes to have IDs if they don't have them
 * @param {Object} session - Session object
 */
export function migrateIconsAndNotesToHaveIds(session) {
  if (!session.hexStates) return;

  for (const hexState of Object.values(session.hexStates)) {
    // Add IDs to icons
    if (hexState.icons) {
      hexState.icons.forEach(icon => {
        if (!icon.id) {
          icon.id = 'icon-' + Math.random().toString(36).substring(2, 11);
        }
      });
    }

    // Add IDs to notes
    if (hexState.notes) {
      hexState.notes.forEach(note => {
        if (!note.id) {
          note.id = 'note-' + Math.random().toString(36).substring(2, 11);
        }
      });
    }
  }

  // Add IDs to tokens
  if (session.tokens) {
    session.tokens.forEach(token => {
      if (!token.id) {
        token.id = 'token-' + Math.random().toString(36).substring(2, 11);
      }
    });
  }
}
